/**
 * Subscription client wrapper around WebSocketClient
 * Provides high-level subscription methods for real-time data
 */

import { WebSocketClient } from "../../transport/websocket/WebSocketClient";
import { fromSatoshi } from "../../utils/math";
import {
  SubscriptionClientConfig,
  SubscriptionCallback,
  ConnectionState,
} from "../../types";

export class SubscriptionClient {
  private wsClient: WebSocketClient;
  private subscriptions: Map<string, SubscriptionCallback[]> = new Map();
  private lastPriceWrappedCallbacks: Map<
    SubscriptionCallback,
    SubscriptionCallback
  > = new Map();
  private wrappedCallbacks: Map<
    string,
    Map<SubscriptionCallback, SubscriptionCallback>
  > = new Map();
  // Multi-feed subscriptions: map of sub_channel -> callbacks
  private multiFeedSubscriptions: Map<
    string,
    SubscriptionCallback[]
  > = new Map();
  private multiFeedHandlerRegistered: boolean = false;

  constructor(config: SubscriptionClientConfig) {
    this.wsClient = new WebSocketClient({
      url: config.url,
      authToken: config.authToken,
      walletAddress: config.walletAddress,
      deviceID: config.deviceID,
    });
  }

  /**
   * Connect to WebSocket server
   */
  async connect(): Promise<void> {
    await this.wsClient.connect();
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.wsClient.disconnect();
    this.subscriptions.clear();
    this.lastPriceWrappedCallbacks.clear();
    this.wrappedCallbacks.clear();
    this.multiFeedSubscriptions.clear();
    this.multiFeedHandlerRegistered = false;
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.wsClient.isConnected();
  }

  /**
   * Get connection state
   */
  getState(): ConnectionState {
    return this.wsClient.getState();
  }

  /**
   * Register a callback for connection state changes
   * Returns an unsubscribe function
   */
  onStateChange(handler: (state: ConnectionState) => void): () => void {
    return this.wsClient.onStateChange(handler);
  }

  /**
   * Set authentication token, address, and device ID
   * Mirrors SetAuthToken from ws_manager.go
   * Delegates to WebSocketClient.setAuthToken
   */
  setAuthToken(token: string, address: string, deviceID: string): void {
    this.wsClient.setAuthToken(token, address, deviceID);
  }

  /**
   * Helper to parse router data field (can be JSON string or object)
   */
  private parseRouterData(data: any): any {
    if (typeof data === "string") {
      try {
        return JSON.parse(data);
      } catch (e) {
        console.error("Failed to parse router data field:", e);
        return null;
      }
    }
    return data;
  }

  /**
   * Helper to extract and parse data from router response
   */
  private extractRouterData(message: any): any | null {
    // Handle Router protocol: { type: "data", channel: "...", data: "..." } or { type: "data", channel: "...", data: {...} }
    if (message.type === "data" && message.data) {
      const parsed = this.parseRouterData(message.data);
      if (!parsed) {
        console.warn("[SubscriptionClient] Failed to parse router data:", message.data);
      }
      return parsed;
    }
    // Handle error responses
    if (message.type === "error" && message.error) {
      console.error("WebSocket error response:", message.error);
      return null;
    }
    // Handle ack responses (silently ignore)
    if (message.type === "ack") {
      return null;
    }
    // Log unexpected message format
    if (message.type !== "data") {
      console.debug("[SubscriptionClient] Unexpected message type:", message.type, "Full message:", message);
    } else if (!message.data) {
      console.warn("[SubscriptionClient] Message type is 'data' but missing data field:", message);
    }
    return null;
  }

  /**
   * Subscribe to orderbook updates
   * @param marketId - Market ID to subscribe to (string or number)
   * @param callback - Callback function for orderbook updates
   * @param convertToDecimal - Whether to convert satoshi to decimal (default: true)
   */
  subscribeOrderbook(
    marketId: number | string,
    callback: SubscriptionCallback,
    convertToDecimal: boolean = true,
  ): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    // Use Router protocol channel name
    const channel = "orderbook";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    // Wrap callback to handle decimal conversion and Router protocol format
    // WebSocketClient now routes type="data" messages by channel name, so we can register directly on channel
    const wrappedCallback = (message: any) => {
      // Extract and parse data from router response
      const orderbookData = this.extractRouterData(message);
      if (!orderbookData) return;

      // Convert satoshi to decimal if needed
      if (
        convertToDecimal &&
        orderbookData.bids &&
        Array.isArray(orderbookData.bids)
      ) {
        orderbookData.bids = orderbookData.bids.map(
          ([price, size]: [string | number, string | number]) => [
            fromSatoshi(price),
            fromSatoshi(size),
          ],
        );
      }
      if (
        convertToDecimal &&
        orderbookData.asks &&
        Array.isArray(orderbookData.asks)
      ) {
        orderbookData.asks = orderbookData.asks.map(
          ([price, size]: [string | number, string | number]) => [
            fromSatoshi(price),
            fromSatoshi(size),
          ],
        );
      }
      callback(orderbookData);
    };

    // Store wrapped callback for proper cleanup
    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    // Register handler on channel name (WebSocketClient routes type="data" messages by channel)
    this.wsClient.on(channel, wrappedCallback);

    // Send subscription message using Router protocol
    try {
      this.wsClient.send({
        type: "subscribe",
        channel: "orderbook",
        params: {
          market_id: marketId.toString(),
        },
      });
    } catch (error) {
      // Clean up on send failure
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from orderbook updates
   */
  unsubscribeOrderbook(
    marketId: number | string,
    callback?: SubscriptionCallback,
  ): void {
    const channel = "orderbook";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        // Remove wrapped callback
        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      // Remove all callbacks
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    // Send unsubscribe message if no more callbacks and connected
    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "orderbook",
          params: {
            market_id: marketId.toString(),
          },
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to trades
   */
  subscribeTrades(
    marketId: number | string,
    callback: SubscriptionCallback,
  ): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    // Use Router protocol channel name
    const channel = "trades";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    // Wrap callback to handle Router protocol format
    // WebSocketClient now routes type="data" messages by channel name, so we can register directly on channel
    const wrappedCallback = (message: any) => {
      const tradesData = this.extractRouterData(message);
      if (tradesData) {
        callback(tradesData);
      }
    };

    // Store wrapped callback for proper cleanup
    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    // Register handler on channel name (WebSocketClient routes type="data" messages by channel)
    this.wsClient.on(channel, wrappedCallback);

    // Send subscription message using Router protocol
    try {
      this.wsClient.send({
        type: "subscribe",
        channel: "trades",
        params: {
          market_id: marketId.toString(),
        },
      });
    } catch (error) {
      // Clean up on send failure
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from trades
   */
  unsubscribeTrades(
    marketId: number | string,
    callback?: SubscriptionCallback,
  ): void {
    const channel = "trades";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        // Remove wrapped callback
        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      // Remove all callbacks
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "trades",
          params: {
            market_id: marketId.toString(),
          },
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to ticker updates (moniker channel)
   * Note: Router uses "moniker" channel name, but we keep "ticker" in the API for consistency
   */
  subscribeTicker(
    marketId: number | string,
    callback: SubscriptionCallback,
  ): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    // Router protocol uses "moniker" channel name, but we track as "ticker" internally
    const routerChannel = "moniker";
    const internalChannel = "ticker";
    this.subscriptions.set(internalChannel, [
      ...(this.subscriptions.get(internalChannel) || []),
      callback,
    ]);

    // Wrap callback to handle Router protocol format
    // WebSocketClient now routes type="data" messages by channel name, so we can register directly on channel
    const wrappedCallback = (message: any) => {
      const tickerData = this.extractRouterData(message);
      if (tickerData) {
        callback(tickerData);
      }
    };

    // Store wrapped callback for proper cleanup
    if (!this.wrappedCallbacks.has(internalChannel)) {
      this.wrappedCallbacks.set(internalChannel, new Map());
    }
    this.wrappedCallbacks.get(internalChannel)!.set(callback, wrappedCallback);

    // Register handler on router channel name (WebSocketClient routes type="data" messages by channel)
    this.wsClient.on(routerChannel, wrappedCallback);

    // Send subscription message using Router protocol (with "moniker" channel name)
    try {
      this.wsClient.send({
        type: "subscribe",
        channel: routerChannel,
        params: {
          market_id: marketId.toString(),
        },
      });
    } catch (error) {
      // Clean up on send failure
      this.wrappedCallbacks.get(internalChannel)?.delete(callback);
      const callbacks = this.subscriptions.get(internalChannel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(internalChannel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from ticker updates
   */
  unsubscribeTicker(
    marketId: number | string,
    callback?: SubscriptionCallback,
  ): void {
    const internalChannel = "ticker";
    const routerChannel = "moniker";
    const callbacks = this.subscriptions.get(internalChannel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(internalChannel, callbacks);

        // Remove wrapped callback
        const wrappedCallback = this.wrappedCallbacks
          .get(internalChannel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(routerChannel, wrappedCallback);
          this.wrappedCallbacks.get(internalChannel)!.delete(callback);
        }
      }
    } else {
      // Remove all callbacks
      const channelWrappedCallbacks =
        this.wrappedCallbacks.get(internalChannel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(routerChannel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(internalChannel);
      }
      this.subscriptions.delete(internalChannel);
    }

    if (
      this.subscriptions.get(internalChannel)?.length === 0 &&
      this.isConnected()
    ) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: routerChannel,
          params: {
            market_id: marketId.toString(),
          },
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to user positions updates
   * Requires authentication (walletAddress must be provided)
   */
  subscribeUserUpdates(callback: SubscriptionCallback): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    // Use "positions" channel which requires address parameter
    const channel = "positions";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    // Wrap callback to handle Router protocol format
    // WebSocketClient now routes type="data" messages by channel name, so we can register directly on channel
    const wrappedCallback = (message: any) => {
      const positionsData = this.extractRouterData(message);
      if (positionsData) {
        callback(positionsData);
      }
    };

    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    // Register handler on channel name (WebSocketClient routes type="data" messages by channel)
    this.wsClient.on(channel, wrappedCallback);

    // Send subscription message - positions channel requires address
    try {
      this.wsClient.send({
        type: "subscribe",
        channel: "positions",
        params: {
          // Note: address should be provided in WebSocketClient config
        },
      });
    } catch (error) {
      // Clean up on send failure
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from user updates
   */
  unsubscribeUserUpdates(callback?: SubscriptionCallback): void {
    const channel = "positions";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "positions",
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to last price updates
   * @param marketId - Market ID to subscribe to (string or number)
   * @param callback - Callback function for last price updates
   * @param convertToDecimal - Whether to convert satoshi to decimal (default: true)
   */
  subscribeLastPrice(
    marketId: number | string,
    callback: SubscriptionCallback,
    convertToDecimal: boolean = true,
  ): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    const channel = "last_price";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    // Wrap callback to handle decimal conversion and Router protocol format
    // WebSocketClient now routes type="data" messages by channel name, so we can register directly on channel
    const wrappedCallback = (message: any) => {
      console.debug("[SubscriptionClient] last_price message received:", message);
      const tradeData = this.extractRouterData(message);
      if (!tradeData) {
        console.warn("[SubscriptionClient] Failed to extract trade data from message:", message);
        return;
      }

      console.debug("[SubscriptionClient] Extracted trade data:", tradeData);

      // Validate required fields
      if (!tradeData.price) {
        console.warn("[SubscriptionClient] Trade data missing price field:", tradeData);
        return;
      }

      // Convert price and quantity from satoshi strings to decimal numbers
      if (convertToDecimal) {
        if (tradeData.price) {
          tradeData.price = fromSatoshi(tradeData.price);
        }
        if (tradeData.quantity) {
          tradeData.quantity = fromSatoshi(tradeData.quantity);
        }
      }

      console.debug("[SubscriptionClient] Calling callback with trade data:", tradeData);
      callback(tradeData);
    };

    // Store wrapped callback for proper cleanup
    this.lastPriceWrappedCallbacks.set(callback, wrappedCallback);

    // Register handler on channel name (WebSocketClient routes type="data" messages by channel)
    this.wsClient.on(channel, wrappedCallback);

    // Send subscription message
    try {
      this.wsClient.send({
        type: "subscribe",
        channel: "last_price",
        params: {
          market_id: marketId.toString(),
        },
      });
    } catch (error) {
      // Clean up on send failure
      this.lastPriceWrappedCallbacks.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from last price updates
   */
  unsubscribeLastPrice(
    marketId: number | string,
    callback?: SubscriptionCallback,
  ): void {
    const channel = "last_price";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        // Remove wrapped callback
        const wrappedCallback = this.lastPriceWrappedCallbacks.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.lastPriceWrappedCallbacks.delete(callback);
        }
      }
    } else {
      // Remove all callbacks
      callbacks.forEach((cb) => {
        const wrappedCallback = this.lastPriceWrappedCallbacks.get(cb);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.lastPriceWrappedCallbacks.delete(cb);
        }
      });
      this.subscriptions.delete(channel);
    }

    // Send unsubscribe message if no more callbacks and connected
    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "last_price",
          params: {
            market_id: marketId.toString(),
          },
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to funding rate updates
   * @param symbol - Symbol to subscribe to (e.g., "BTC/USDT")
   * @param callback - Callback function for funding rate updates
   */
  subscribeFundingRate(
    symbol: string,
    callback: SubscriptionCallback,
  ): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    const channel = "funding_rate";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    // Wrap callback to handle Router protocol format
    const wrappedCallback = (message: any) => {
      const fundingRateData = this.extractRouterData(message);
      if (fundingRateData) {
        callback(fundingRateData);
      }
    };

    // Store wrapped callback for proper cleanup
    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    // Register handler on channel name
    this.wsClient.on(channel, wrappedCallback);

    // Send subscription message using Router protocol
    try {
      this.wsClient.send({
        type: "subscribe",
        channel: "funding_rate",
        params: {
          symbol: symbol,
        },
      });
    } catch (error) {
      // Clean up on send failure
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from funding rate updates
   * @param symbol - Symbol to unsubscribe from (e.g., "BTC/USDT")
   * @param callback - Optional callback to remove specific subscription
   */
  unsubscribeFundingRate(
    symbol: string,
    callback?: SubscriptionCallback,
  ): void {
    const channel = "funding_rate";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        // Remove wrapped callback
        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      // Remove all callbacks
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    // Send unsubscribe message if no more callbacks and connected
    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "funding_rate",
          params: {
            symbol: symbol,
          },
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to buckets (user's margin buckets)
   * Requires authentication (walletAddress must be provided)
   * @param callback - Callback function for buckets updates
   */
  subscribeBuckets(callback: SubscriptionCallback): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    const channel = "buckets";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    const wrappedCallback = (message: any) => {
      const bucketsData = this.extractRouterData(message);
      if (bucketsData) {
        callback(bucketsData);
      }
    };

    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    this.wsClient.on(channel, wrappedCallback);

    try {
      this.wsClient.send({
        type: "subscribe",
        channel: "buckets",
        params: {
          // Note: address should be provided in WebSocketClient config
        },
      });
    } catch (error) {
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from buckets updates
   */
  unsubscribeBuckets(callback?: SubscriptionCallback): void {
    const channel = "buckets";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "buckets",
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to deviation updates (price deviation between perp and spot)
   * @param symbol - Symbol to subscribe to (e.g., "BTC/USDC")
   * @param callback - Callback function for deviation updates
   */
  subscribeDeviation(
    symbol: string,
    callback: SubscriptionCallback,
  ): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    const channel = "deviation";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    const wrappedCallback = (message: any) => {
      const deviationData = this.extractRouterData(message);
      if (deviationData) {
        callback(deviationData);
      }
    };

    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    this.wsClient.on(channel, wrappedCallback);

    try {
      this.wsClient.send({
        type: "subscribe",
        channel: "deviation",
        params: {
          symbol: symbol,
        },
      });
    } catch (error) {
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from deviation updates
   */
  unsubscribeDeviation(
    symbol: string,
    callback?: SubscriptionCallback,
  ): void {
    const channel = "deviation";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "deviation",
          params: {
            symbol: symbol,
          },
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to index price updates (oracle index price)
   * @param symbol - Symbol to subscribe to (e.g., "BTC/USD" or "BTC/USDC" for cross-rate)
   * @param callback - Callback function for index price updates
   */
  subscribeIndexPrice(
    symbol: string,
    callback: SubscriptionCallback,
  ): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    const channel = "index_price";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    const wrappedCallback = (message: any) => {
      const indexPriceData = this.extractRouterData(message);
      if (indexPriceData) {
        callback(indexPriceData);
      }
    };

    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    this.wsClient.on(channel, wrappedCallback);

    try {
      this.wsClient.send({
        type: "subscribe",
        channel: "index_price",
        params: {
          symbol: symbol,
        },
      });
    } catch (error) {
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from index price updates
   */
  unsubscribeIndexPrice(
    symbol: string,
    callback?: SubscriptionCallback,
  ): void {
    const channel = "index_price";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "index_price",
          params: {
            symbol: symbol,
          },
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to kline (OHLC candlestick) updates
   * @param marketId - Market ID to subscribe to (string or number)
   * @param interval - Interval for kline data (required: "1m", "5m", "15m", "1h", "4h", "1d")
   * @param callback - Callback function for kline updates
   */
  subscribeKline(
    marketId: number | string,
    interval: string,
    callback: SubscriptionCallback,
  ): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    if (!interval) {
      throw new Error("Interval is required for kline subscription");
    }

    const channel = "kline";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    const wrappedCallback = (message: any) => {
      const klineData = this.extractRouterData(message);
      if (klineData) {
        callback(klineData);
      }
    };

    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    this.wsClient.on(channel, wrappedCallback);

    try {
      this.wsClient.send({
        type: "subscribe",
        channel: "kline",
        params: {
          market_id: marketId.toString(),
          interval: interval,
        },
      });
    } catch (error) {
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from kline updates
   */
  unsubscribeKline(
    marketId: number | string,
    interval: string,
    callback?: SubscriptionCallback,
  ): void {
    const channel = "kline";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "kline",
          params: {
            market_id: marketId.toString(),
            interval: interval,
          },
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to mark price updates (mark price for liquidations)
   * @param symbol - Symbol to subscribe to (e.g., "BTC/USDC")
   * @param callback - Callback function for mark price updates
   */
  subscribeMarkPrice(
    symbol: string,
    callback: SubscriptionCallback,
  ): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    const channel = "mark_price";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    const wrappedCallback = (message: any) => {
      const markPriceData = this.extractRouterData(message);
      if (markPriceData) {
        callback(markPriceData);
      }
    };

    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    this.wsClient.on(channel, wrappedCallback);

    try {
      this.wsClient.send({
        type: "subscribe",
        channel: "mark_price",
        params: {
          symbol: symbol,
        },
      });
    } catch (error) {
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from mark price updates
   */
  unsubscribeMarkPrice(
    symbol: string,
    callback?: SubscriptionCallback,
  ): void {
    const channel = "mark_price";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "mark_price",
          params: {
            symbol: symbol,
          },
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to price feed updates (oracle price feeds)
   * @param symbol - Symbol to subscribe to (e.g., "BTC/USDT")
   * @param callback - Callback function for price feed updates
   */
  subscribePriceFeed(
    symbol: string,
    callback: SubscriptionCallback,
  ): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    const channel = "price_feed";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    const wrappedCallback = (message: any) => {
      const priceFeedData = this.extractRouterData(message);
      if (priceFeedData) {
        callback(priceFeedData);
      }
    };

    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    this.wsClient.on(channel, wrappedCallback);

    try {
      this.wsClient.send({
        type: "subscribe",
        channel: "price_feed",
        params: {
          symbol: symbol,
        },
      });
    } catch (error) {
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from price feed updates
   */
  unsubscribePriceFeed(
    symbol: string,
    callback?: SubscriptionCallback,
  ): void {
    const channel = "price_feed";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "price_feed",
          params: {
            symbol: symbol,
          },
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to TWAP (Time-Weighted Average Price) updates
   * @param symbol - Symbol to subscribe to (e.g., "BTC/USDC")
   * @param windowSize - Time window in seconds (e.g., 300 for 5 minutes)
   * @param callback - Callback function for TWAP updates
   */
  subscribeTWAP(
    symbol: string,
    windowSize: number,
    callback: SubscriptionCallback,
  ): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    if (!windowSize || windowSize <= 0) {
      throw new Error("Window size must be a positive number");
    }

    const channel = "twap";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    const wrappedCallback = (message: any) => {
      const twapData = this.extractRouterData(message);
      if (twapData) {
        callback(twapData);
      }
    };

    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    this.wsClient.on(channel, wrappedCallback);

    try {
      this.wsClient.send({
        type: "subscribe",
        channel: "twap",
        params: {
          symbol: symbol,
          window_size: windowSize,
        },
      });
    } catch (error) {
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from TWAP updates
   */
  unsubscribeTWAP(
    symbol: string,
    windowSize: number,
    callback?: SubscriptionCallback,
  ): void {
    const channel = "twap";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "twap",
          params: {
            symbol: symbol,
            window_size: windowSize,
          },
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to multi-feed channel
   * Multi-feed provides a single subscription that streams updates from multiple sub-channels
   * @param marketId - Market ID to subscribe to (string or number)
   * @param symbol - Symbol to subscribe to (e.g., "BTC-USDC-PERP")
   * @param callback - Callback function for multi-feed updates (receives { sub_channel, data })
   * @param interval - Optional interval for kline data (e.g., "1m", "5m", "15m", "1h", "4h", "1d")
   */
  subscribeMultiFeed(
    marketId: number | string,
    symbol: string,
    callback: SubscriptionCallback,
    interval?: string,
  ): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    const channel = "multi_feed";
    
    // Register multi-feed message handler if not already registered
    if (!this.multiFeedHandlerRegistered) {
      const multiFeedHandler = (message: any) => {
        // Handle multi-feed messages: { type: "data", channel: "multi_feed", sub_channel: "...", data: {...} }
        if (message.type === "data" && message.channel === "multi_feed" && message.sub_channel) {
          const subChannel = message.sub_channel;
          
          // Get callbacks for this specific sub-channel
          const subChannelCallbacks = this.multiFeedSubscriptions.get(subChannel) || [];
          
          // Get all multi-feed callbacks (for general multi-feed subscription)
          const multiFeedCallbacks = this.subscriptions.get(channel) || [];
          
          // Extract data from message - for multi-feed, data is already in message.data
          let data = message.data;
          if (typeof data === "string") {
            try {
              data = JSON.parse(data);
            } catch (e) {
              console.error("Failed to parse multi-feed data field:", e);
              return;
            }
          }
          
          if (data) {
            // Call sub-channel specific callbacks with just the data
            subChannelCallbacks.forEach((cb) => {
              try {
                cb(data);
              } catch (error) {
                console.error(`Error in multi-feed sub-channel callback for ${subChannel}:`, error);
              }
            });
            
            // Call general multi-feed callbacks with sub_channel info
            multiFeedCallbacks.forEach((cb) => {
              try {
                cb({ sub_channel: subChannel, data });
              } catch (error) {
                console.error(`Error in multi-feed callback:`, error);
              }
            });
          }
        }
      };

      this.wsClient.on(channel, multiFeedHandler);
      this.multiFeedHandlerRegistered = true;
    }

    // Store callback - we'll route by sub_channel in the handler
    // For now, store with a special key to track multi-feed subscriptions
    if (!this.subscriptions.has(channel)) {
      this.subscriptions.set(channel, []);
    }
    this.subscriptions.get(channel)!.push(callback);

    // Send subscription message
    try {
      const params: any = {
        market_id: marketId.toString(),
        symbol: symbol,
      };
      if (interval) {
        params.interval = interval;
      }

      this.wsClient.send({
        type: "subscribe",
        id: `multi_feed_${marketId}_${symbol}`,
        channel: "multi_feed",
        params: params,
      });
    } catch (error) {
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Subscribe to a specific sub-channel within multi-feed
   * This is a convenience method that filters multi-feed messages for a specific sub-channel
   * @param marketId - Market ID to subscribe to (string or number)
   * @param symbol - Symbol to subscribe to (e.g., "BTC-USDC-PERP")
   * @param subChannel - Sub-channel name (e.g., "balances", "orders", "positions", "orderbook", etc.)
   * @param callback - Callback function for sub-channel updates
   * @param interval - Optional interval for kline data
   */
  subscribeMultiFeedSubChannel(
    marketId: number | string,
    symbol: string,
    subChannel: string,
    callback: SubscriptionCallback,
    interval?: string,
  ): void {
    // Register callback for this sub-channel
    if (!this.multiFeedSubscriptions.has(subChannel)) {
      this.multiFeedSubscriptions.set(subChannel, []);
    }
    this.multiFeedSubscriptions.get(subChannel)!.push(callback);

    // Subscribe to multi-feed if not already subscribed
    const multiFeedCallbacks = this.subscriptions.get("multi_feed") || [];
    if (multiFeedCallbacks.length === 0) {
      // Create a dummy callback to trigger multi-feed subscription
      // The actual routing will be handled by the multi-feed handler
      const dummyCallback = () => {
        // This callback won't be called directly, but it ensures multi-feed is subscribed
      };
      this.subscribeMultiFeed(marketId, symbol, dummyCallback, interval);
    }
    // If already subscribed, the multi-feed handler will route messages to sub-channel callbacks
  }

  /**
   * Unsubscribe from multi-feed channel
   */
  unsubscribeMultiFeed(
    marketId: number | string,
    symbol: string,
    callback?: SubscriptionCallback,
  ): void {
    const channel = "multi_feed";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
    } else {
      this.subscriptions.delete(channel);
    }

    // If no more multi-feed subscriptions, remove handler
    if (this.subscriptions.get(channel)?.length === 0) {
      // Note: We can't easily remove the handler without tracking it, so we'll leave it
      // The handler will just have no callbacks to call
      this.multiFeedSubscriptions.clear();
    }

    // Send unsubscribe message if no more callbacks and connected
    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          id: `multi_feed_${marketId}_${symbol}`,
          channel: "multi_feed",
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Unsubscribe from a specific multi-feed sub-channel
   */
  unsubscribeMultiFeedSubChannel(
    subChannel: string,
    callback?: SubscriptionCallback,
  ): void {
    const callbacks = this.multiFeedSubscriptions.get(subChannel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        if (callbacks.length === 0) {
          this.multiFeedSubscriptions.delete(subChannel);
        } else {
          this.multiFeedSubscriptions.set(subChannel, callbacks);
        }
      }
    } else {
      this.multiFeedSubscriptions.delete(subChannel);
    }
  }

  /**
   * Subscribe to balances updates (authenticated channel)
   * Requires authentication (walletAddress must be provided)
   * @param callback - Callback function for balances updates
   */
  subscribeBalances(callback: SubscriptionCallback): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    const channel = "balances";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    const wrappedCallback = (message: any) => {
      const balancesData = this.extractRouterData(message);
      if (balancesData) {
        callback(balancesData);
      }
    };

    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    this.wsClient.on(channel, wrappedCallback);

    try {
      this.wsClient.send({
        type: "subscribe",
        channel: "balances",
        params: {
          // Note: address should be provided in WebSocketClient config
        },
      });
    } catch (error) {
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from balances updates
   */
  unsubscribeBalances(callback?: SubscriptionCallback): void {
    const channel = "balances";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "balances",
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }

  /**
   * Subscribe to orders updates (authenticated channel)
   * Requires authentication (walletAddress must be provided)
   * @param callback - Callback function for orders updates
   */
  subscribeOrders(callback: SubscriptionCallback): void {
    if (!this.isConnected()) {
      throw new Error("WebSocket not connected. Call connect() first.");
    }

    const channel = "orders";
    this.subscriptions.set(channel, [
      ...(this.subscriptions.get(channel) || []),
      callback,
    ]);

    const wrappedCallback = (message: any) => {
      const ordersData = this.extractRouterData(message);
      if (ordersData) {
        callback(ordersData);
      }
    };

    if (!this.wrappedCallbacks.has(channel)) {
      this.wrappedCallbacks.set(channel, new Map());
    }
    this.wrappedCallbacks.get(channel)!.set(callback, wrappedCallback);

    this.wsClient.on(channel, wrappedCallback);

    try {
      this.wsClient.send({
        type: "subscribe",
        channel: "orders",
        params: {
          // Note: address should be provided in WebSocketClient config
        },
      });
    } catch (error) {
      this.wrappedCallbacks.get(channel)?.delete(callback);
      const callbacks = this.subscriptions.get(channel) || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);
      }
      throw error;
    }
  }

  /**
   * Unsubscribe from orders updates
   */
  unsubscribeOrders(callback?: SubscriptionCallback): void {
    const channel = "orders";
    const callbacks = this.subscriptions.get(channel) || [];

    if (callback) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
        this.subscriptions.set(channel, callbacks);

        const wrappedCallback = this.wrappedCallbacks
          .get(channel)
          ?.get(callback);
        if (wrappedCallback) {
          this.wsClient.off(channel, wrappedCallback);
          this.wrappedCallbacks.get(channel)!.delete(callback);
        }
      }
    } else {
      const channelWrappedCallbacks = this.wrappedCallbacks.get(channel);
      if (channelWrappedCallbacks) {
        channelWrappedCallbacks.forEach((wrappedCallback) => {
          this.wsClient.off(channel, wrappedCallback);
        });
        this.wrappedCallbacks.delete(channel);
      }
      this.subscriptions.delete(channel);
    }

    if (this.subscriptions.get(channel)?.length === 0 && this.isConnected()) {
      try {
        this.wsClient.send({
          type: "unsubscribe",
          channel: "orders",
        });
      } catch (error) {
        console.error("Failed to send unsubscribe message:", error);
      }
    }
  }
}
